import dayjs from 'dayjs'
import { VFC, useState } from 'react'
import ReactSlick, { Settings as ReactSlickSettings } from 'react-slick'
import { usePremiumContributions } from 'src/api/premiumContribution'
import { postInterested } from 'src/api/recommendation'
import { useLoginUser } from 'src/api/user'
import {
  CONTRIBUTION_BUDGET,
  CONTRIBUTION_USER_CIRCLE,
} from 'src/assets/images'
import {
  LeftRoundedArrowIcon,
  PremiumContributionIcon,
  PremiumContributionPriceIcon,
  PremiumContributionUserIcon,
  RightRoundedArrowIcon,
} from 'src/assets/svgs'
import { SmallButton } from 'src/elements/Button'
import { PremiumContribution } from 'src/models/PremiumContribution'
import { fontWeightBold } from 'src/styles/font'
import { inversing } from 'src/styles/mixins'
import styled from 'styled-components'
import {
  _contributionPremiumBorder,
  _contributionPremiumDetailValue,
  _contributionsPremiumBackground,
  _menuorange,
  contributionGray,
  truewhite,
} from '../../styles/colors'
import { SalesDemoModal } from '../Messages/Modals/SalesDemoModal'
import { SubInformation } from './ContributionCard'
import { PremiumContributionModal } from './PremiumContributionModal'

const TITLE_LIMIT = 62 // タイトルを切り取る長さ
const CONTENT_LIMIT = 92 // コンテンツを切り取る長さ

export const PremiumContributions: VFC = ({}) => {
  const { user } = useLoginUser()
  const { data, mutate } = usePremiumContributions()
  const premiumContributions = data?.premiumContributions || []

  const canShowArrows = premiumContributions.length > 3
  const slidesToShow = Math.min(3, premiumContributions.length)
  const slidesToScroll = 3

  const DEFAULT_PREMIUM_CONTRIBUTION: PremiumContribution = {
    id: 0,
    companyId: 0,
    title: '',
    content: '',
    status: 'publish',
    purpose: 'buy',
    startAt: dayjs(),
    endAt: dayjs(),
    isAnonymous: false,
    employeeNumberText: '',
    salesScaleText: '',
    companyName: '',
    companyAdminName: '',
    shouldDisplayCloseLabel: false,
    isApplied: false,
    displayBudgetText: '',
    daysLeftText: '',
    positionText: null,
    contentTags: [],
    typeOfBusiness: null,
  }
  const [premiumContributionForModal, setPremiumContributionForModal] =
    useState<PremiumContribution>(DEFAULT_PREMIUM_CONTRIBUTION)
  const [isOpenPremiumContributionModal, setIsOpenPremiumContributionModal] =
    useState<boolean>(false)
  const [showSalesDemoModal, setShowSalesDemoModal] = useState<boolean>(false)

  const onClickModal = (premiumContribution: PremiumContribution) => {
    if (user?.plan.salesDemo) setShowSalesDemoModal(true)
    else if (hasPaid) {
      setIsOpenPremiumContributionModal(true)
      setPremiumContributionForModal(premiumContribution)
    }
    // NOTE: 無料会員の場合は何もしない（ボタンは非表示になる）
  }
  const onCloseModal = () => {
    setIsOpenPremiumContributionModal(false)
    setPremiumContributionForModal(DEFAULT_PREMIUM_CONTRIBUTION)
  }
  const onSubmitPostInterested = (
    companyId: number,
    reason: string,
    detail: string,
    premiumContributionId: number,
  ) => {
    postInterested(
      companyId,
      reason,
      undefined,
      detail,
      premiumContributionId,
    ).then(() => {
      mutate()
      onCloseModal()
    })
  }

  const hasPaid = user?.plan.paid || false

  if (!premiumContributions.length) return <></>

  return (
    <>
      <PremiumContributionDiv>
        <PremiumContributionDescription>
          <Flex>
            <PremiumContributionIcon />
            <PremiumContributionTitle>
              プレミアム55投稿
            </PremiumContributionTitle>
          </Flex>
          <PremiumContributionText>
            売上規模5億、または従業員数50名以上の企業様からの相談・発注ニーズをONLYSTORYプラットフォーム独占で期間限定にて掲載！
          </PremiumContributionText>
        </PremiumContributionDescription>
        <PremiumContributionCardsSection>
          <ReactSlick
            arrows={canShowArrows}
            slidesToShow={slidesToShow}
            slidesToScroll={slidesToScroll}
            {...settings}
          >
            {premiumContributions.map((premiumContribution) => (
              <SlickItem key={premiumContribution.id}>
                <PremiumContributionCard>
                  <PremiumContributionTop
                    hasPaid={hasPaid}
                    premiumContribution={premiumContribution}
                  />
                  <PremiumContributionDetail>
                    <PremiumContributionUserInfo>
                      <UserInfo
                        isModal={false}
                        premiumContribution={premiumContribution}
                      />
                    </PremiumContributionUserInfo>
                    <PremiumContributionContent>
                      {premiumContribution.content.length > CONTENT_LIMIT ? (
                        <div>
                          {premiumContribution.content.slice(0, CONTENT_LIMIT)}…
                        </div>
                      ) : (
                        <div>{premiumContribution.content}</div>
                      )}
                    </PremiumContributionContent>
                  </PremiumContributionDetail>
                  <PremiumContributionBottom>
                    <PremiumContributionDate
                      premiumContribution={premiumContribution}
                    />
                    <PremiumContributionBottomLeftDays>
                      {premiumContribution.daysLeftText}
                    </PremiumContributionBottomLeftDays>
                    {premiumContribution.shouldDisplayCloseLabel ? (
                      <DisableButton disabled>募集終了</DisableButton>
                    ) : premiumContribution.isApplied ? (
                      <DisableButton disabled>応募済み</DisableButton>
                    ) : hasPaid ? (
                      <ApplyButton
                        onClick={() => onClickModal(premiumContribution)}
                      >
                        募集中
                      </ApplyButton>
                    ) : null}
                  </PremiumContributionBottom>
                </PremiumContributionCard>
              </SlickItem>
            ))}
          </ReactSlick>
        </PremiumContributionCardsSection>
      </PremiumContributionDiv>
      {isOpenPremiumContributionModal && (
        <PremiumContributionModal
          premiumContribution={premiumContributionForModal}
          close={() => onCloseModal()}
          onSubmitPostInterested={onSubmitPostInterested}
          hasPaid={hasPaid}
        />
      )}
      {showSalesDemoModal && (
        <SalesDemoModal close={() => setShowSalesDemoModal(false)} />
      )}
    </>
  )
}

type PremiumContributionTopProps = {
  premiumContribution: PremiumContribution
  hasPaid: boolean
  shouldcollapse?: boolean
}
export const PremiumContributionTop: VFC<PremiumContributionTopProps> = ({
  premiumContribution,
  hasPaid,
  shouldcollapse = true,
}) => (
  <>
    <PremiumContributionCardTitle>
      {shouldcollapse && premiumContribution.title.length > TITLE_LIMIT ? (
        <>{premiumContribution.title.slice(0, TITLE_LIMIT)}…</>
      ) : (
        premiumContribution.title
      )}
    </PremiumContributionCardTitle>
    <SubInformation
      typeOfPurchase={premiumContribution.purpose}
      name={premiumContribution.companyAdminName}
      companyName={premiumContribution.companyName}
      // NOTE: 無料会員の場合は必ず匿名化 or 有料会員でも匿名投稿の場合は匿名化
      isAnonymous={!hasPaid ? true : premiumContribution.isAnonymous}
    />
  </>
)

type UserInfoProps = {
  premiumContribution: PremiumContribution
  isModal: boolean
}
export const UserInfo: VFC<UserInfoProps> = ({
  premiumContribution,
  isModal = true,
}) => {
  return (
    <>
      <UserInfoRow
        isModal={isModal}
        keyName={premiumContribution.displayBudgetText}
        value={`予算`}
      />
      <UserInfoRow
        isModal={isModal}
        keyName={premiumContribution.salesScaleText}
        value={`売上規模`}
      />
      <UserInfoRow
        isModal={isModal}
        isPriceIcon={false}
        keyName={premiumContribution.employeeNumberText}
        value={`従業員数`}
      />
    </>
  )
}

type UserInfoRowProps = {
  isModal: boolean
  isPriceIcon?: boolean
  keyName: string
  value: string
}
const UserInfoRow: VFC<UserInfoRowProps> = ({
  isModal,
  isPriceIcon = true,
  keyName,
  value,
}) => {
  return (
    // NOTE: アイコンの出し分けを行う。 モーダルであるか or Priceアイコンを表示すべきか？
    <PremiumContributionUserInfoRow>
      {isModal ? (
        isPriceIcon ? (
          <img src={CONTRIBUTION_BUDGET} alt="" width={20} height={20} />
        ) : (
          <img src={CONTRIBUTION_USER_CIRCLE} alt="" width={20} height={20} />
        )
      ) : isPriceIcon ? (
        <div style={{ height: '20px', width: '20px' }}>
          <PremiumContributionPriceIcon />
        </div>
      ) : (
        <PremiumContributionUserIcon />
      )}
      <PremiumContributionDetailKey>{keyName}</PremiumContributionDetailKey>
      <PremiumContributionDetailValue>/ {value}</PremiumContributionDetailValue>
    </PremiumContributionUserInfoRow>
  )
}

type PremiumContributionDateProps = {
  premiumContribution: PremiumContribution
}
export const PremiumContributionDate: VFC<PremiumContributionDateProps> = ({
  premiumContribution,
}) => (
  <PremiumContributionBottomDate>
    {premiumContribution.startAt.format('YYYY/MM/DD')}
    &nbsp;-&nbsp;
    {premiumContribution.endAt.format('YYYY/MM/DD')}
  </PremiumContributionBottomDate>
)

const Flex = styled.div`
  display: flex;
`

const PremiumContributionDiv = styled.div`
  min-height: 434px;
  height: 100%;
  background-color: ${_contributionsPremiumBackground};
  padding-top: 30px;
  padding-bottom: 30px;
`

const PremiumContributionDescription = styled.div`
  margin-left: 88px;
  margin-right: 88px;
  margin-bottom: 24px;
`

const PremiumContributionTitle = styled.h1`
  padding-left: 8px;
  font-size: 22px;
  line-height: 35px;
  font-weight: ${fontWeightBold};
`

const PremiumContributionText = styled.p`
  padding-top: 8px;
  font-size: 13px;
  color: ${contributionGray};
  font-weight: ${fontWeightBold};
`

const PremiumContributionCardsSection = styled.section`
  .slick-arrow {
    transform: translate(-50%, -50%);
    font-size: 3em;
    z-index: 1;
    color: inherit;
    :hover {
      opacity: 0.5;
      color: inherit;
    }
    width: 1em;
    height: 1em;
    &.slick-prev {
      left: 89%;
      top: -18%;
    }
    &.slick-next {
      right: 2%;
      top: -18%;
    }
  }
`

const settings: ReactSlickSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  centerMode: true,
  initialSlide: 1,
  nextArrow: <RightRoundedArrowIcon />,
  prevArrow: <LeftRoundedArrowIcon />,
}

const SlickItem = styled.div`
  margin: 0 auto;
  padding: 0 10px;
`

const PremiumContributionCard = styled.div`
  background: ${truewhite};
  max-width: 559px;
  width: 100%;
  min-height: 277px;
  height: 100%;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 2px 2px 8px 0px rgba(0, 0, 0, 0.1);
`

const PremiumContributionCardTitle = styled.h1`
  min-height: 48px;
  font-size: 16px;
  font-weight: ${fontWeightBold};
  line-height: 24px;
`

const PremiumContributionDetail = styled.div`
  margin-top: 14px;
  display: flex;
  padding-bottom: 12px;
  height: 96px;
`

const PremiumContributionUserInfo = styled.div`
  display: flex;
  flex-direction: column;
`
const PremiumContributionContent = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  overflow: hidden;
`
const PremiumContributionUserInfoRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 10px;
  width: 180px;
`
const PremiumContributionDetailKey = styled.span`
  margin-left: 8px;
  margin-right: 2px;
  font-weight: ${fontWeightBold};
  font-size: 13px;
`
const PremiumContributionDetailValue = styled.span`
  margin-right: 16px;
  font-weight: ${fontWeightBold};
  font-size: 10px;
  color: ${_contributionPremiumDetailValue};
  white-space: nowrap;
`
const PremiumContributionBottom = styled.div`
  border-top: 1px solid ${_contributionPremiumBorder};
  padding-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const PremiumContributionBottomDate = styled.div`
  color: ${contributionGray};
  font-size: 10px;
  line-height: 1.2em;
`
const PremiumContributionBottomLeftDays = styled.div`
  font-size: 14px;
  font-weight: ${fontWeightBold};
  white-space: nowrap;
  margin: auto 8px;
`

const DisableButton = styled(SmallButton)`
  width: 147px;
  color: ${truewhite};
  background: ${_contributionPremiumDetailValue};
`

const ApplyButton = styled(SmallButton)`
  width: 147px;
  border: solid ${_menuorange} 1px;

  ${inversing({ color: truewhite, background: _menuorange })};
`
